import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Flag, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const COPY = {
  contact: { eyebrow: "Contact", title: "Say hello.", blurb: "Questions about the project, ideas, encouragement, or something that isn't working — we read every message.", Icon: Mail },
  correction: { eyebrow: "Report a Correction", title: "Help us get it right.", blurb: "Spotted a typo, a wrong reference, a misattributed word study or a commentary that doesn't match the passage? Tell us where and we'll fix it.", Icon: Flag },
};

export default function Contact() {
  const [sp, setSp] = useSearchParams();
  const topic = sp.get("topic") === "correction" ? "correction" : "contact";
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", reference: sp.get("ref") || "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const c = COPY[topic];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/contact", { ...form, topic, page: document.referrer || null });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8" data-testid="contact-page" data-topic={topic}>
      <Seo title={topic === "correction" ? "Report a Correction" : "Contact"} description={c.blurb} />
      <div className="mb-8 flex overflow-hidden rounded-full border border-border bg-background font-mono text-[11px] uppercase tracking-wider" data-testid="contact-topic-switch">
        {[["contact", "Contact"], ["correction", "Report a correction"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setSp(k === "contact" ? {} : { topic: k })} aria-pressed={topic === k} data-testid={`contact-topic-${k}`}
            className={`flex-1 py-2.5 transition-colors ${topic === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{c.eyebrow}</span>
      <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{c.title}</h1>
      <p className="mt-3 text-base text-muted-foreground">{c.blurb}</p>

      {sent ? (
        <div className="mt-10 rounded-2xl border border-border bg-card p-8 text-center" data-testid="contact-success">
          <CheckCircle2 className="mx-auto h-8 w-8 text-gold" />
          <h2 className="mt-3 font-serif text-2xl font-semibold text-foreground">Thank you — we got it.</h2>
          <p className="mt-2 text-sm text-muted-foreground">{topic === "correction" ? "We'll review the passage and make the fix." : "We'll reply if your message needs one."}</p>
          <Button asChild variant="outline" className="mt-6 rounded-full"><Link to="/explore">Back to the questions</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="contact-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Your name</span>
              <Input required value={form.name} onChange={set("name")} className="mt-1" data-testid="contact-name" /></label>
            <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</span>
              <Input required type="email" value={form.email} onChange={set("email")} className="mt-1" data-testid="contact-email" /></label>
          </div>
          {topic === "correction" && (
            <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Where is it? (reference, question or page)</span>
              <Input value={form.reference} onChange={set("reference")} placeholder="e.g. Luke 2:49 · John Gill commentary · Word study for 'nagad'" className="mt-1" data-testid="contact-reference" /></label>
          )}
          <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{topic === "correction" ? "What should it say instead?" : "Message"}</span>
            <Textarea required minLength={10} rows={6} value={form.message} onChange={set("message")} className="mt-1" data-testid="contact-message" /></label>
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
          {error && <p className="text-sm text-destructive" data-testid="contact-error">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="contact-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <c.Icon className="mr-2 h-4 w-4" />} {topic === "correction" ? "Send correction" : "Send message"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
